import { bjToast, Button } from '@components/common';
import { PlusIcon } from '@heroicons/react/20/solid';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import {
  ApiJobTitleAutocompleteOk,
  fetcher, isApiV1Errors, buildSearchParams,
} from '@utils/data-fetching';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';

/**
 * @description Banner component for adding a job from talent search .
 */

interface IProps {
  keyword: string;
  locale: Locale;
}

export const BannerForJobAdd = ({ keyword, locale }: IProps) => {
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();

  const [templateId, setTemplateId] = useState<string>('');


  const getMessageContent = useCallback(() => {
    fetcher<ApiJobTitleAutocompleteOk[]>(`/job-title-autocomplete?term=${keyword}`, locale, { method: 'GET' })
      .then((response) => {
        if (isApiV1Errors(response)) {
          bjToast.error(response.errors[0].message);
        }
        if (response && response[0]) {
          setTemplateId(response[0]?.id);
        } else {
          setTemplateId('');
        }
      }).catch(() => {});
  }, [locale, keyword]);

  useEffect(() => {
    if (keyword) { getMessageContent(); }
  }, [keyword, getMessageContent]);

  const handleCtaClick = () => {
    sendGoogleTagEvent('add_job_cta_teaser_click', 'Custom');
    router.push(`/job-add/edit${buildSearchParams({ previousJobId: templateId, keyword })}`);
  };

  return (
    <div className="my-3 rounded-lg border border-dotted border-secondary bg-surface p-2 md:rounded-full md:p-4">
      <div className="flex flex-wrap items-center justify-between">
        <div className="flex items-center">
          <div className="mr-2 mt-1 w-fit rounded-full bg-secondary/20 p-2 md:mr-4 md:mt-0">
            <Button onClick={handleCtaClick} className="gt-click-from-banner-add-job !p-1" styling="solid" size="xs" color="secondary" rounding="full">
              <PlusIcon className="size-5 stroke-2 md:size-6" />
            </Button>
          </div>
          <div>
            <h3 className="text-sm font-bold text-ink md:text-md">
              <Trans
                i18nKey="banner.add.job.by.keyword.title"
                values={{
                  keyword,
                }}
              />
            </h3>
            <p className="m-0 text-sm font-thin text-ink md:text-md">{t('banner.add.job.by.keyword.description')}</p>
          </div>
        </div>
        <Button onClick={handleCtaClick} className="gt-click-from-banner-add-job hidden lg:block" styling="solid" size="sm" color="secondary" rounding="full">
          {t('banner.add.job.by.keyword.buttonText')}
        </Button>
      </div>
    </div>
  );
};
